// src/components/proposal-formats/FormatBaseAmountEditor.tsx

import { useCallback, useEffect, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import { getFormatSettings, upsertFormatSettings } from "@/lib/opportunity/formatSettingsClient";
import { DEFAULT_BASE_AMOUNT_LABEL } from "@/lib/opportunity/formatSettingsTypes";

export type FormatBaseAmountEditorProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  reloadTick: number;
  onSaved: () => void;
  embedded?: boolean;
};

export function FormatBaseAmountEditor(props: FormatBaseAmountEditorProps) {
  const { formatKey, productType, reloadTick, onSaved, embedded } = props;
  const [label, setLabel] = useState(DEFAULT_BASE_AMOUNT_LABEL);
  const [storedLabel, setStoredLabel] = useState(DEFAULT_BASE_AMOUNT_LABEL);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getFormatSettings({ format_key: formatKey, product_type: productType });
    setLoading(false);
    const next = r.ok && r.data.settings?.base_amount_label
      ? r.data.settings.base_amount_label
      : DEFAULT_BASE_AMOUNT_LABEL;
    setLabel(next);
    setStoredLabel(next);
  }, [formatKey, productType]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const dirty = label.trim() !== storedLabel;

  async function handleSave() {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSaving(true);
    setMsg(null);
    const r = await upsertFormatSettings({
      format_key: formatKey,
      product_type: productType,
      base_amount_label: trimmed,
    });
    setSaving(false);
    if (r.ok) {
      setStoredLabel(r.data.settings.base_amount_label);
      setLabel(r.data.settings.base_amount_label);
      setMsg({ ok: true, text: "Base amount label saved." });
      onSaved();
    } else {
      setMsg({ ok: false, text: r.error });
    }
  }

  return (
    <div className={embedded ? "space-y-2" : "rounded-2xl border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-2"}>
      <div className="flex items-center justify-between gap-2">
        {embedded ? null : (
          <>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              Base amount
            </h4>
            <span className="text-[10px] text-gray-400 dark:text-zinc-500">Required · not removable</span>
          </>
        )}
      </div>
      {!embedded ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          Renamable starter line reps fill in at quote time (e.g. &quot;Nucor Building Materials&quot;).
        </p>
      ) : (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          Renamable starter line for reps at quote time.
        </p>
      )}
      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <>
          <label className="block text-xs">
            <span className="text-gray-500">Line label</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
            />
          </label>
          <div className="flex items-center justify-end gap-2">
            {msg ? (
              <span className={msg.ok ? "text-emerald-600 text-xs" : "text-red-600 text-xs"}>
                {msg.text}
              </span>
            ) : null}
            <button
              type="button"
              disabled={saving || !dirty || !label.trim()}
              onClick={() => void handleSave()}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save label"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
